import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { formatChartValue } from '../../utils/chartFormatters';

/**
 * Decumulation (Drawdown) Chart
 * Shows portfolio balance decline during retirement with income and expenses
 */
export default function DecumulationChart({ data, statePension, inputs, currencySymbol = "$", country = 'US' }) {
  if (!data || !data.projections || data.projections.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Retirement Drawdown</CardTitle>
          <CardDescription>No data available</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Labels based on country
  const statePensionLabel = country === 'UK' ? 'Your State Pension' : 'Your Social Security';

  // Transform data for chart
  const chartData = data.projections.map(yearData => ({
    year: yearData.year,
    age: yearData.age,
    balance: yearData.totalBalance,
    withdrawal: yearData.actualWithdrawal,
    statePension: yearData.statePension,
    taxFreeLumpSum: yearData.taxFreeLumpSum || 0,
    rentalIncome: yearData.rentalIncome || 0,
    partTimeIncome: yearData.partTimeIncome || 0,
    annuityIncome: yearData.annuityIncome || 0,
    otherPassiveIncome: yearData.otherPassiveIncome || 0,
    spouseIncome: yearData.spouseIncome || 0,
    totalIncome: yearData.totalIncome,
    essentialExpenses: yearData.expenses.essential,
    totalExpenses: yearData.expenses.essential + yearData.expenses.discretionary
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Find the projection data by matching the age (label)
      const yearData = data.projections.find(p => p.age === label);
      if (!yearData) return null;

      return (
        <div className="bg-white p-4 border border-gray-200 rounded shadow-lg max-w-sm">
          <p className="font-semibold mb-2">Age {yearData.age} (Year {yearData.year})</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-blue-600">Portfolio Balance:</span>
              <span className="font-medium">
                {currencySymbol}{yearData.totalBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>

            {/* Income Sources */}
            <div className="pt-2 mt-2 border-t">
              <div className="font-semibold text-gray-700 mb-1">Income Sources:</div>
              <div className="flex justify-between gap-4">
                <span className="text-green-600 pl-2">Portfolio Withdrawal:</span>
                <span className="font-medium">
                  {currencySymbol}{yearData.actualWithdrawal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </div>
              {yearData.statePension > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="text-purple-600 pl-2">{statePensionLabel}:</span>
                  <span className="font-medium">
                    {currencySymbol}{yearData.statePension.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              )}
              {yearData.rentalIncome > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="text-orange-600 pl-2">Rental Income:</span>
                  <span className="font-medium">
                    {currencySymbol}{yearData.rentalIncome.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              )}
              {yearData.partTimeIncome > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="text-teal-600 pl-2">Part-Time Work:</span>
                  <span className="font-medium">
                    {currencySymbol}{yearData.partTimeIncome.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              )}
              {yearData.annuityIncome > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="text-indigo-600 pl-2">Annuity Income:</span>
                  <span className="font-medium">
                    {currencySymbol}{yearData.annuityIncome.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              )}
              {yearData.otherPassiveIncome > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="text-pink-600 pl-2">Other Income:</span>
                  <span className="font-medium">
                    {currencySymbol}{yearData.otherPassiveIncome.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              )}
              {yearData.spouseIncome > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="text-rose-600 pl-2">Spouse Income:</span>
                  <span className="font-medium">
                    {currencySymbol}{yearData.spouseIncome.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              )}
              {yearData.taxFreeLumpSum > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="text-yellow-600 pl-2">Tax-Free Lump Sum:</span>
                  <span className="font-medium">
                    {currencySymbol}{yearData.taxFreeLumpSum.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              )}
            </div>

            <div className="flex justify-between gap-4 pt-2 border-t font-semibold">
              <span className="text-gray-700">Total Income:</span>
              <span className="font-bold text-green-700">
                {currencySymbol}{yearData.totalIncome.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="flex justify-between gap-4 border-b pb-2">
              <span className="text-red-600">Total Expenses:</span>
              <span className="font-medium text-red-700">
                {currencySymbol}{(yearData.expenses.essential + yearData.expenses.discretionary + (yearData.oneOffExpense || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="flex justify-between gap-4 font-semibold">
              <span className="text-gray-700">Net Cashflow:</span>
              <span className={`font-bold ${(yearData.totalIncome - yearData.expenses.essential - yearData.expenses.discretionary - (yearData.oneOffExpense || 0)) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {currencySymbol}{(yearData.totalIncome - yearData.expenses.essential - yearData.expenses.discretionary - (yearData.oneOffExpense || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Determine if plan succeeded
  const success = data.success;
  const finalAge = data.projections[data.projections.length - 1]?.age;
  const depletionAge = data.depletionAge;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Retirement Drawdown Phase</CardTitle>
        <CardDescription>
          Portfolio balance and withdrawals from age {data.projections[0]?.age} to {finalAge}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Balance Over Time */}
        <div className="mb-8">
          <h4 className="text-sm font-semibold mb-3">Portfolio Balance</h4>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="age"
                  label={{ value: 'Age', position: 'insideBottom', offset: -15 }}
                />
                <YAxis
                  label={{ value: 'Portfolio Balance', angle: -90, position: 'insideLeft' }}
                  tickFormatter={(value) => formatChartValue(value, currencySymbol)}
                  width={100}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="balance"
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.3}
                  name="Portfolio Balance"
                />
                {!success && (
                  <ReferenceLine
                    x={depletionAge}
                    stroke="red"
                    strokeDasharray="3 3"
                    label={{ value: 'Depleted', position: 'top', fill: 'red' }}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Income vs Expenses */}
        <div className="mb-8">
          <h4 className="text-sm font-semibold mb-3">Annual Income Sources vs Expenses</h4>
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="age"
                  label={{ value: 'Age', position: 'insideBottom', offset: -5 }}
                />
                <YAxis
                  label={{ value: 'Amount', angle: -90, position: 'insideLeft' }}
                  tickFormatter={(value) => formatChartValue(value, currencySymbol)}
                  width={100}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />

                {/* Income Sources */}
                <Line
                  type="monotone"
                  dataKey="withdrawal"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  name="Portfolio Withdrawal"
                />
                {statePension > 0 && (
                  <Line
                    type="monotone"
                    dataKey="statePension"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    dot={false}
                    name={statePensionLabel}
                  />
                )}
                {chartData.some(d => d.taxFreeLumpSum > 0) && (
                  <Line
                    type="monotone"
                    dataKey="taxFreeLumpSum"
                    stroke="#eab308"
                    strokeWidth={2}
                    dot={false}
                    name="Tax-Free Lump Sum"
                  />
                )}
                {chartData.some(d => d.rentalIncome > 0) && (
                  <Line
                    type="monotone"
                    dataKey="rentalIncome"
                    stroke="#f97316"
                    strokeWidth={2}
                    dot={false}
                    name="Rental Income"
                  />
                )}
                {chartData.some(d => d.partTimeIncome > 0) && (
                  <Line
                    type="monotone"
                    dataKey="partTimeIncome"
                    stroke="#14b8a6"
                    strokeWidth={2}
                    dot={false}
                    name="Part-Time Work"
                  />
                )}
                {chartData.some(d => d.annuityIncome > 0) && (
                  <Line
                    type="monotone"
                    dataKey="annuityIncome"
                    stroke="#6366f1"
                    strokeWidth={2}
                    dot={false}
                    name="Annuity Income"
                  />
                )}
                {chartData.some(d => d.otherPassiveIncome > 0) && (
                  <Line
                    type="monotone"
                    dataKey="otherPassiveIncome"
                    stroke="#ec4899"
                    strokeWidth={2}
                    dot={false}
                    name="Other Income"
                  />
                )}
                {chartData.some(d => d.spouseIncome > 0) && (
                  <Line
                    type="monotone"
                    dataKey="spouseIncome"
                    stroke="#f43f5e"
                    strokeWidth={2}
                    dot={false}
                    name="Spouse Income"
                  />
                )}

                {/* Expenses */}
                <Line
                  type="monotone"
                  dataKey="totalExpenses"
                  stroke="#ef4444"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Total Expenses"
                />
                <Line
                  type="monotone"
                  dataKey="essentialExpenses"
                  stroke="#f59e0b"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  dot={false}
                  name="Essential Expenses"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
          <div>
            <div className="text-sm text-muted-foreground">Starting Balance</div>
            <div className="text-2xl font-bold">
              {currencySymbol}{data.projections[0]?.totalBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">
              {success ? 'Final Balance' : 'Balance at Depletion'}
            </div>
            <div className={`text-2xl font-bold ${success ? 'text-green-600' : 'text-red-600'}`}>
              {currencySymbol}{data.finalBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">
              {success ? 'Plan Status' : 'Depleted At Age'}
            </div>
            <div className={`text-2xl font-bold ${success ? 'text-green-600' : 'text-red-600'}`}>
              {success ? '✓ Success' : depletionAge}
            </div>
          </div>
        </div>

        {/* Strategy Info */}
        <div className="mt-6 pt-4 border-t">
          <h4 className="text-sm font-semibold mb-2">Withdrawal Strategy</h4>
          <div className="text-sm text-muted-foreground">
            {getStrategyDescription(inputs.withdrawalStrategy)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function getStrategyDescription(strategy) {
  const descriptions = {
    fixed: 'Fixed dollar amount each year (not adjusted for inflation)',
    percentage: 'Fixed 4% of current portfolio value each year',
    inflationAdjusted: 'Initial withdrawal amount adjusted annually for inflation',
    guardrails: 'Dynamic withdrawals that adjust based on portfolio performance (Guyton-Klinger)',
    dynamic: 'Withdrawals dynamically adjusted based on portfolio health',
    essentialOnly: 'Only essential spending withdrawals (safety mode)'
  };

  return descriptions[strategy] || strategy;
}
